import { Button, Col, Form, Modal, ModalBody, ModalHeader, Row, Input } from "reactstrap"
import React, { forwardRef, useImperativeHandle, useState } from "react"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup";
import { Schema } from "yup"
import { apiClient } from "../../infra/api-client"
import { BASE_URL } from "../../global"
import { SessionStudent } from "../../modules/session/session-student"
import { LessonActivityOnetime } from "../../modules/session/activity/lesson-activity.types"

type SelectOption<T extends String | number> = {
  label: string;
  value: T;
}


const validationRules: Record<keyof FormValues, Schema> = {
  subject: Yup.object()
    .required("Subject is required"),
  duration: Yup.object()
    .required("Duration is required"),
  coins: Yup.object()
    .required("coins is required"),
  activity_name: Yup.string()
    .required("Activity name is required"),
  description: Yup.string(),
  comments: Yup.string()
    .nullable(),
};
const validationSchema  = Yup.object(validationRules)

type FormValues = {
  subject?: SelectOption<number>;
  activity_name?: string;
  description?: string;
  comments?: string;
  duration?: SelectOption<string>
  coins?: SelectOption<string>
}

interface CreateOneTimeActivityDialogProps {

}

interface DialogInput {
  studentKey: string;
  sessionKey: string;
  subjectOptions: SelectOption<number>[];
  durationOptions: SelectOption<string>[];
  coinsOptions: SelectOption<string>[];
  isHomework: boolean;
  sessionStudent: SessionStudent
  isCreateMode: boolean
  activity?: LessonActivityOnetime
}

type DialogFunctions = {
  open: (input: DialogInput) => Promise<boolean>,
}
export type ManageOneTimeActivityDialogFunctions = DialogFunctions;

async function submitCreateOneTimeActivity(formValues: FormValues, input: DialogInput): Promise<void> {
  return apiClient.post(BASE_URL + "sessionActivity/addOnetimeActivity", {
    studentKey: input.studentKey,
    sessionKey: input.sessionKey,
    tutorKey: input.sessionStudent.fk_tut_key,
    subjectId: formValues.subject?.value,
    name: formValues.activity_name,
    description: formValues.description,
    duration: formValues.duration?.value,
    comments: formValues.comments,
    coins: formValues.coins?.value,
    isHomework: input.isHomework
  }).then((response) => {
    return response.data
  })

}

async function submitUpdateOneTimeActivity(formValues: FormValues, input: DialogInput): Promise<void> {
  return apiClient.post(BASE_URL + "sessionActivity/updateOnetimeActivity", {
    onetimeActivityKey: input.activity?.activity_node[0].pk_onetime_activity_key,
    lessonActivityKey: input.activity?.pk_lesson_activity_key,
    studentKey: input.studentKey,
    subjectId: formValues.subject?.value,
    name: formValues.activity_name,
    description: formValues.description,
    duration: formValues.duration?.value,
    comments: formValues.comments,
    coins: formValues.coins?.value
  }).then((response) => {
    return response.data
  })
}


export const ManageOneTimeActivityDialog = forwardRef((props: CreateOneTimeActivityDialogProps, ref) => {

  const [open, setOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [input, setInput] = useState<DialogInput>();
  const [resolver, setResolver] = useState<(value: boolean)=>void>();
  const [isCreate, setIsCreate] = useState<boolean>(false);

  const openDialog = (input: DialogInput)=>{
    setIsCreate(input.isCreateMode)
    setInput(input);
    const result = new Promise<boolean>(resolve => {
      setResolver(()=> resolve)
    });
    resetForm(input);
    setOpen(true);
    return result;
  }

  const resetForm = (input: DialogInput)=>{

    if(!input.isCreateMode) {
      const node = input.activity?.activity_node[0]!;
      formik.resetForm({
        values: {
          activity_name: node.name,
          comments: input.activity?.tutor_comments,
          duration: input.durationOptions.find( e=> e.value === node.duration),
          coins: input.coinsOptions.find( e=> e.value === node.coins.toString()),
          description: node.description,
          subject: input.subjectOptions.find( e=> e.value === input.activity?.fk_sub_id)
        }
      })
    }
    else {
      formik.resetForm()
    }

  }

  const exposedFunctions: ManageOneTimeActivityDialogFunctions = {
    open: openDialog,
  }

  useImperativeHandle(ref, () => (exposedFunctions));

  const onSubmit = async (values: FormValues)=>{
    if(isCreate) {
      await createOnetimeActivity();
    }
    else {
      await editOnetimeActivity();
    }
    if(resolver) {
      setOpen(false)
      resolver(true)
    }
  }

  const createOnetimeActivity = async () => {
    const result = await submitCreateOneTimeActivity(formik.values, input!);
  }

  const editOnetimeActivity = async () => {
    const result = await submitUpdateOneTimeActivity(formik.values, input!);
  }

  const formik = useFormik<FormValues>({
    initialValues: {},
    validateOnChange: true,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
  })

  const cancel = ()=>{
    setOpen(false)
    if(resolver){
      resolver(false)
    }
  }

  const closeBtn = (
    <button type="button" onClick={cancel}
        style={{position: "absolute", right: "20px"}}>
      X
    </button>
  );


  // @ts-ignore
  return (<>
    <Modal isOpen={open} size="lg">
      <ModalHeader className="mt-0" style={{width: '100%'}} close={closeBtn}>
        Add One Time Activity (Task)
      </ModalHeader>
      <ModalBody>
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3 g-3">
            <Col className="col-6">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Subject<span style={{ color: "red" }}>*</span>
                </label>
                <div className="">
                  <Select
                    menuPlacement="auto"
                    options={input?.subjectOptions}
                    name="subject"
                    value={formik.values.subject}
                    onChange={ val=>formik.setFieldValue('subject', val)}
                  />
                </div>
                <ErrorMsg formik={formik} field="subject"/>
              </div>
            </Col>

            <Col className="col-6">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Activity Name
                  <span style={{ color: "red" }}>*</span>
                </label>
                <div className="">
                  <Input
                    name="activity_name"
                    type="text"
                    value={formik.values.activity_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="form-control"
                  />
                </div>
                <ErrorMsg formik={formik} field="activity_name"/>
              </div>
            </Col>

            <Col className="col-12">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Description
                </label>
                <div className="">
                    <textarea
                      name="description"
                      placeholder=""
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      rows={4}
                      className="form-control"
                    />

                </div>
                <ErrorMsg formik={formik} field="description"/>
              </div>
            </Col>

            <Col className="col-6">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Duration<span style={{ color: "red" }}>*</span>
                </label>
                <div className="">
                  <Select
                    menuPlacement="auto"
                    options={input?.durationOptions}
                    name="duration"
                    value={formik.values.duration}
                    onChange={ val=>formik.setFieldValue('duration', val)}
                  />
                </div>
                <ErrorMsg formik={formik} field="duration"/>
              </div>
            </Col>

            <Col className="col-6">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Coins<span style={{ color: "red" }}>*</span>
                </label>
                <div className="">
                  <Select
                    menuPlacement="auto"
                    options={input?.coinsOptions}
                    name="coins"
                    value={formik.values.coins}
                    onChange={ val=>formik.setFieldValue('coins', val)}
                  />
                </div>
                <ErrorMsg formik={formik} field="coins"/>
              </div>
            </Col>

            <Col className="col-12">
              <div className="">
                <label
                  htmlFor="example-text-input"
                  className="col-form-label"
                >
                  Tutor Comment
                </label>
                <div className="">
                  <Input
                    type="text"
                    value={formik.values.comments}
                    onChange={formik.handleChange}
                    placeholder="Tutor comments.."
                    name="comments"
                    className="form-control"
                  />
                </div>
                <ErrorMsg formik={formik} field="comments"/>
              </div>
            </Col>

          </Row>
          <div className="text-end mt-4" style={{ display: 'flex', justifyContent: 'end' }}>
            <Button
              type="submit"
              variant="contained"
              color="danger"
              disabled={isSubmitting}
            >
              {isCreate ? 'Add' : 'Update'}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>

  </>)

})


type Formik = ReturnType<typeof useFormik<FormValues>>
const ErrorMsg = (props: {formik: Formik, field: keyof FormValues})=> {

  const {formik} = props;
  const touched = formik.touched;
  const errors = formik.errors;

  return (
    <>
      {
        (formik.submitCount>0 || (touched[props.field] && errors[props.field])) &&
        <div style={{
          color: '#ff0000'
        }}>{
          errors[props.field]
        }</div>
      }
    </>
  )
}

